import { Helmet } from 'react-helmet';
import { LoaderFunctionArgs, redirect, useLoaderData } from 'react-router';

import { defaultNext } from '@/auth';
import CognitoMfaSetup from '@/auth/components/Login/CognitoMfaSetup';
import { isCognitoSessionActive } from '@/auth/utils/cognitoSession';
import { cognitoEmail, hasCognitoTotp } from '@/auth/utils/cognitoSignIn';
import { safeNextPath } from '@/auth/utils/mfaGate';
import BodyContainer from '@/components/BodyContainer';
import LayoutPage from '@/components/Layout/LayoutPage';

interface LoaderData {
  next: string;
  enrolled: boolean;
  email: string;
}

/**
 * ENG-6103: where the api's MFA gate sends a Cognito user with no factor
 * (src/auth/utils/mfaGate.ts). Without a Cognito session there is nothing to
 * enrol, so it goes to the login page.
 */
export async function mfaSetupPageLoader({
  request,
}: LoaderFunctionArgs): Promise<LoaderData | Response> {
  if (!isCognitoSessionActive()) return redirect('/login');
  const next =
    safeNextPath(new URL(request.url).searchParams.get('next'), defaultNext) ?? defaultNext;
  // A user who enrolled and reloaded only waits for the gate to open.
  // Nothing here reads the viewer: the gate refuses all but a few of its
  // fields, and boot loaded none of them.
  // A failed preference lookup is not "not enrolled": treating it so would
  // send an enrolled user through setup again. It goes to the route's error
  // handling, where a reload retries.
  const [enrolled, email] = await Promise.all([
    hasCognitoTotp(),
    cognitoEmail().catch(() => undefined),
  ]);
  return { next, enrolled, email: email ?? '' } satisfies LoaderData;
}

export default function MfaSetupPage(): JSX.Element {
  const { next, enrolled, email } = useLoaderData() as LoaderData;
  return (
    <LayoutPage>
      <Helmet>
        <title>Set Up Two-Factor Authentication | OnFrontiers</title>
      </Helmet>
      <BodyContainer>
        <CognitoMfaSetup
          email={email}
          next={next}
          enrolled={enrolled}
          // The session itself is no longer usable: end it and sign in again.
          onExpired={() => window.location.assign('/logout')}
        />
      </BodyContainer>
    </LayoutPage>
  );
}
